import { useId, useState } from 'react';
import Menu from 'storybookConfig/stories/cells/Menu';
import { useAppSelector } from 'store';
import { selectIsSeller } from 'store/selectors/me/company';
import Body from 'storybookConfig/stories/molecules/Body';
import Collapse, { CollapseChevron } from 'storybookConfig/stories/molecules/Collapse';
import { SubLinkContainer } from './Shared.styles';
import Badge from 'storybookConfig/stories/molecules/Badge';
import useFlagEnabled from 'hooks/useFlagEnabled';

interface DropshipLinksProps {
  isActive: (path: string) => boolean;
  partnerCount: number;
}

const getPartnersMenuLabel = (isSeller: boolean) => (isSeller ? 'Retailers' : 'Suppliers');

const DropshipLinks = ({ isActive, partnerCount }: DropshipLinksProps) => {
  const showMarketplace = useFlagEnabled('showMarketplace');
  const [expanded, setExpanded] = useState(true);
  const dropshipId = useId();
  const isSeller = useAppSelector(selectIsSeller);
  const partnersMenuLabel = getPartnersMenuLabel(isSeller);

  if (!showMarketplace) {
    // Render the original links directly, no dropdown
    return (
      <>
        <Menu.Link $iconName="download" to="/orders" $isActive={isActive('/orders')}>
          Orders
        </Menu.Link>
        <Menu.Link $iconName="receipt-text" to="/invoices" $isActive={isActive('/invoices')}>
          Invoices
        </Menu.Link>
        <Menu.Link $iconName="blocks" to="/products" $isActive={isActive('/products')}>
          Products
        </Menu.Link>
        {isSeller && (
          <Menu.Link $iconName="receipt" to="/prices" $isActive={isActive('/prices')}>
            Price Lists
          </Menu.Link>
        )}
        <Menu.Link to="/partners" $iconName="users" $isActive={isActive('/partners')}>
          {partnersMenuLabel}
          <Badge variant="info" count={partnerCount} data-testid="partner-count-badge" />
        </Menu.Link>
      </>
    );
  }

  // New dropdown UI for flagged users
  return (
    <>
      <Menu.LinkButton
        aria-expanded={expanded}
        aria-controls={dropshipId}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <Body variant="Body/Header">Dropship</Body>
        <CollapseChevron open={expanded} />
      </Menu.LinkButton>
      <Collapse id={dropshipId} open={expanded}>
        <SubLinkContainer>
          <Menu.Link $iconName="download" to="/orders" $isActive={isActive('/orders')}>
            Orders
          </Menu.Link>
          <Menu.Link $iconName="receipt-text" to="/invoices" $isActive={isActive('/invoices')}>
            Invoices
          </Menu.Link>
          <Menu.Link $iconName="blocks" to="/products" $isActive={isActive('/products')}>
            Products
          </Menu.Link>
          {isSeller && (
            <Menu.Link $iconName="receipt" to="/prices" $isActive={isActive('/prices')}>
              Price Lists
            </Menu.Link>
          )}
          <Menu.Link to="/partners" $iconName="users" $isActive={isActive('/partners')}>
            {partnersMenuLabel}
            <Badge variant="info" count={partnerCount} data-testid="partner-count-badge" />
          </Menu.Link>
          <Menu.Link to="/requests/active" $iconName="mail" $isActive={isActive('/requests/:type')}>
            Requests
            <Badge variant="info" count={4} data-testid="partner-count-badge" />
          </Menu.Link>
        </SubLinkContainer>
      </Collapse>
    </>
  );
};

export default DropshipLinks;
